import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useCustomer } from '../api/queries'
import { useAuth } from '../auth/AuthProvider'
import { DeleteCustomerCard } from '../components/DeleteCustomerCard'
import { ProjectCard } from '../components/ProjectCard'
import { AddressLink, PhoneLink } from '../components/SiteContact'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { CustomerForm } from './CustomersPage'
import { DevicesByAddress } from './DevicesPage'
import { useDevices } from '../api/queries'

/** Kundekort med kontaktoplysninger og kundens sager. */
export function CustomerDetailPage() {
  const { id } = useParams()
  const { data, isLoading, error } = useCustomer(Number(id))
  const { isProjectManager, isSuperadmin } = useAuth()
  const [editing, setEditing] = useState(false)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <EmptyState>Kunden blev ikke fundet.</EmptyState>

  const { customer, projects } = data

  if (editing) {
    return <CustomerForm customer={customer} onClose={() => setEditing(false)} />
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card
        title={customer.name}
        actions={
          isProjectManager ? (
            <button className="btn btn--sm" onClick={() => setEditing(true)}>
              Rediger
            </button>
          ) : undefined
        }
      >
        {!customer.isActive && (
          <div className="error-box" style={{ marginBottom: 12, background: '#FFF7ED', borderColor: '#FED7AA', color: '#9A3412' }}>
            Kunden er markeret som inaktiv.
          </div>
        )}

        <div className="form-grid">
          <div>
            <div className="tiny muted">Kontaktperson</div>
            <div className="small">{customer.contactPerson ?? '-'}</div>
          </div>
          <div>
            <div className="tiny muted">Telefon</div>
            <div className="small">
              <PhoneLink phone={customer.phone} />
            </div>
          </div>
          <div>
            <div className="tiny muted">E-mail</div>
            <div className="small">
              {customer.email ? <a href={`mailto:${customer.email}`}>{customer.email}</a> : '-'}
            </div>
          </div>
          <div>
            <div className="tiny muted">Adresse</div>
            <div className="small">
              <AddressLink contact={customer} />
            </div>
          </div>
          {/* Hvad installationen paa kundens adresse har ret til (beslutning 109). */}
          <div>
            <div className="tiny muted">Rettigheder</div>
            <div className="small">{customer.connectionAmps ? `${customer.connectionAmps} A` : '-'}</div>
          </div>
          {customer.reference && (
            <div>
              <div className="tiny muted">Kundens reference</div>
              <div className="small">{customer.reference}</div>
            </div>
          )}
        </div>

        {customer.notes && (
          <p className="small" style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>
            {customer.notes}
          </p>
        )}
      </Card>

      <div>
        <div className="section__header">
          <h2 className="section__title">Sager</h2>
          <span className="section__count">{projects.length}</span>
        </div>
        {projects.length === 0 ? (
          <EmptyState>Der er ingen sager på denne kunde endnu.</EmptyState>
        ) : (
          <div className="grid grid--projects">
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </div>

      {/* Kundens enheder samlet pr. adresse (beslutning 109). */}
      <CustomerDevices customerId={customer.id} />

      {/* Kun Superadmin sletter (beslutning 079). Administratorer og projektledere
          deaktiverer i stedet under Rediger. */}
      {isSuperadmin && <DeleteCustomerCard customer={customer} />}
    </div>
  )
}

/** Kundens enheder, samlet pr. adresse (beslutning 109). */
function CustomerDevices({ customerId }: { customerId: number }) {
  const { data, isLoading } = useDevices({ customerId })

  return (
    <div>
      <div className="section__header">
        <h2 className="section__title">Enheder</h2>
        <span className="section__count">{data?.length ?? ''}</span>
      </div>
      {isLoading ? (
        <Loading />
      ) : !data || data.length === 0 ? (
        <EmptyState>Der står ingen enheder hos kunden endnu.</EmptyState>
      ) : (
        <DevicesByAddress devices={data} />
      )}
    </div>
  )
}
